<template>
  <div class="fb-tabs js-ibh-fastbooking-tabs" role="tablist" :aria-label="props.i18n.aria_label.booking_type">
    <button
      id="tab-hotel"
      class="fb-tabs__tab"
      role="tab"
      :aria-selected="activeTab === 'hotel' ? 'true' : 'false'"
      :aria-controls="props.i18n.aria_label.hotel_tab"
      :tabindex="activeTab === 'hotel' ? 0 : -1"
      :class="{ 'fb-tabs__tab--active': activeTab == 'hotel' }"
      @keydown="onKeydown($event, 'hotel')"
      @click="handleTabChange('hotel')"
    >
      <i class="s s-bed-hotel fb-tabs__icon"></i>
      {{ props.i18n.fastbooking.hotel }}
    </button>

    <button
      v-if="showFlightHotelTab"
      id="tab-flight-plus-hotel"
      class="fb-tabs__tab fb-tabs__tab"
      role="tab"
      :aria-selected="activeTab === 'flight-plus-hotel' ? 'true' : 'false'"
      :aria-controls="props.i18n.aria_label.flight_tab"
      :tabindex="activeTab === 'flight-plus-hotel' ? 0 : -1"
      :class="{ 'fb-tabs__tab--active': activeTab == 'flight-plus-hotel' }"
      @keydown="onKeydown($event, 'flight-plus-hotel')"
      @click="handleTabChange('flight-plus-hotel')"
    >
      <i class="s s-flight-hotel fb-tabs__icon--flight"></i>
      {{ flightPlusHotelData.flight_and_hotel.label }}
    </button>
  </div>
  <FlightPlusHotelModal :i18n="i18n" :show-modal="showModal" @close="closeFlightAndHotelModal" @accept="handleAccept" />
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'

import { fastbookingTabsEvents } from '../../../../shared/fastbooking/fastbooking.analytics'
import fastbooking from '../../../../shared/fastbooking/ibh_fastbooking'

import FlightPlusHotelModal from './FlightPlusHotelModal.vue'

const props = defineProps({
  i18n: {
    type: Object,
    default: () => {},
  },
  activeTab: {
    type: String,
    default: 'hotel',
  },
  flightPlusHotelData: {
    type: Object,
    default: () => {},
  },
})

const emit = defineEmits(['update:tabs'])

const showModal = ref(false)
const showFlightHotelTab = computed(() => {
  return props.flightPlusHotelData?.flight_and_hotel
})

const isDestinia = computed(() => {
  return props.flightPlusHotelData?.flight_and_hotel.provider === 'Destinia'
})

function handleTabChange(tab) {
  if (isDestinia.value) {
    fastbooking.setTab(tab)
    emit('update:tabs', tab)
    const eventAct = tab === 'hotel' ? 'hotel' : 'flight and hotel'
    fastbookingTabsEvents(eventAct)
  } else {
    showModal.value = true
  }
}
function onKeydown(event, tabId) {
  // tabId is 'hotel' o 'flight-plus-hotel' actual tab
  const tabs = Array.from(document.querySelectorAll('[role="tab"]'))
  const currentActiveTabIndex = tabs.findIndex(t => t.id === `tab-${props.activeTab}`)

  let nextTabButton

  switch (event.key) {
    case 'ArrowRight':
      event.preventDefault()
      nextTabButton = tabs[(currentActiveTabIndex + 1) % tabs.length]
      break
    case 'ArrowLeft':
      event.preventDefault()
      nextTabButton = tabs[(currentActiveTabIndex - 1 + tabs.length) % tabs.length]
      break
    case 'Home':
      event.preventDefault()
      nextTabButton = tabs[0]
      break
    case 'End':
      event.preventDefault()
      nextTabButton = tabs[tabs.length - 1]
      break
    case 'Enter':
    case ' ': //space button
      event.preventDefault() // prevent scroll when press space

      if (tabId !== props.activeTab) {
        handleTabChange(tabId)
      }
      nextTick(() => {
        const activeTabButton = document.getElementById(`tab-${tabId}`)
        if (activeTabButton) {
          activeTabButton.focus()
        }
      })
      return
    default:
      return
  }

  if (nextTabButton) {
    const nextTabId = nextTabButton.id.replace('tab-', '')
    handleTabChange(nextTabId)
  }
}

function closeFlightAndHotelModal() {
  showModal.value = false
}
function handleAccept() {
  window.location.href = props.flightPlusHotelData.utag_flight_and_hotel_link
}
</script>

<style lang="scss">
@use '../../../../base_css/abstracts/abstracts.scss' as *;

.fb-tabs {
  position: relative;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: -1px;

  &__tab {
    position: relative;
    cursor: pointer;
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    padding: 10px 30px;
    background-color: var(--c_grey_100);
    color: var(--c_grey_600);
    font-weight: bold;
    font-size: 14px;
  }

  &__icon {
    font-size: 24px;

    &--flight {
      font-size: 20px;
    }
  }
}

.ada {
  .fb-tabs {
    &__tab {
      &:focus {
        background-color: var(--fastbooking-background-color-focus);
        color: var(--c_turquoise);
      }
    }
  }
}

@include commonTabletDesktop {
  .fb-tabs {
    &__tab {
      &--active {
        background-color: var(--c_white);
        color: var(--c_turquoise);
      }
    }
  }
}

@include mobileStyles {
  .fb-tabs {
    &__tab {
      width: 50%;
      padding: 10px 0;
      border: 1px solid var(--c_grey);
      border-top: 2px solid transparent;
      font-size: 12px;
      text-transform: uppercase;

      &--active {
        border: none;
        border-top: 2px solid var(--c_dark_grey);
        background-color: var(--c_white);
        color: var(--c_dark_grey);
      }
    }
  }
}
</style>
